<template><div><code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__header">
      <div class="d-headline--md">
        Lorem ipsum
      </div>
      <button type="button" class="d-btn d-btn--xs d-btn--circle">
        <dt-icon name="more-vertical" size="200" />
      </button>
    </div>
    <div class="d-card__content">
      <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
    </div>
    <div class="d-card__footer">
      <div class="d-d-flex d-g8">
        <button type="button" class="d-btn d-btn--sm d-btn--outlined">Button</button>
      </div>
    </div>
  </div>
</code-well-header>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Cards are surfaces that display content and actions on a single topic.
They should be easy to scan for relevant and actionable information. Elements, like text and images, should be placed on them in a way that clearly indicates hierarchy.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">To display content and actions on a single topic.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Add too many call-to-action elements to the same card. A card should only contain a single primary action.</li>
<li class="d-docsite--list-element">Inform users about important changes.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">It should only contain a single idea that may feature a call-to-action, or the option to navigate to more detailed content.</li>
<li class="d-docsite--list-element">The content of a card should be concise and offer only a preview of detailed content.</li>
<li class="d-docsite--list-element">The headings should set clear expectations about the card’s purpose.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="base" tabindex="-1" class="d-docsite--header-3">Base <a class="header-anchor d-link d-docsite--link d-link" href="#base" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__header">
      <div>(header slot)</div>
    </div>
    <div class="d-card__content">
      <div>(content slot)</div>
    </div>
    <div class="d-card__footer">
      <div>(footer slot)</div>
    </div>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-card d-w264">
  <div class="d-card__header">
    <div>(header slot)</div>
  </div>
  <div class="d-card__content">
    <div>(content slot)</div>
  </div>
  <div class="d-card__footer">
    <div>(footer slot)</div>
  </div>
</div>
'
vueCode='
<dt-card class="d-w264">
  <template #header>
    (header slot)
  </template>
  <template #content>
    (content slot)
  </template>
  <template #footer>
    (footer slot)
  </template>
</dt-card>
'
showHtmlWarning />
<h3 id="with-header" tabindex="-1" class="d-docsite--header-3">With Header <a class="header-anchor d-link d-docsite--link d-link" href="#with-header" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__header">
      <div class="d-headline--md">
        Lorem ipsum
      </div>
      <button type="button" class="d-btn d-btn--xs d-btn--circle">
        <dt-icon name="more-vertical" size="200" />
      </button>
    </div>
    <div class="d-card__content">
      <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
    </div>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-card d-w264">
  <div class="d-card__header">
    <div class="d-headline--md">
      Lorem ipsum
    </div>
    <button type="button" class="d-btn d-btn--xs d-btn--circle">
      <dt-icon name="more-vertical" size="200" />
    </button>
  </div>
  <div class="d-card__content">
    <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
  </div>
</div>
'
vueCode='
<dt-card class="d-w264">
  <template #header>
    <p class="d-headline-medium">Lorem ipsum</p>
      <dt-button
        size="xs"
        importance="clear"
        aria-label="Menu button"
      >
        <template #icon>
          <dt-icon
            name="more-vertical"
            size="100"
          />
        </template>
      </dt-button>
  </template>
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
</dt-card>
'
showHtmlWarning />
<h3 id="with-footer" tabindex="-1" class="d-docsite--header-3">With Footer <a class="header-anchor d-link d-docsite--link d-link" href="#with-footer" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__content">
      <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
    </div>
    <div class="d-card__footer">
      <div class="d-d-flex d-g8">
        <button type="button" class="d-btn d-btn--sm d-btn--outlined">Button</button>
      </div>
    </div>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-card d-w264">
  <div class="d-card__content">
    <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
  </div>
  <div class="d-card__footer">
    <div class="d-d-flex d-g8">
      <button type="button" class="d-btn d-btn--sm d-btn--outlined">Button</button>
    </div>
  </div>
</div>
'
vueCode='
<dt-card class="d-w264">
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
  <template #footer>
    <dt-button
      importance="outlined"
      size="sm"
    >
      Button
    </dt-button>
  </template>
</dt-card>
'
showHtmlWarning />
<h3 id="content-only" tabindex="-1" class="d-docsite--header-3">Content Only <a class="header-anchor d-link d-docsite--link d-link" href="#content-only" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__content">
      <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
    </div>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-card d-w264">
  <div class="d-card__content">
    <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
  </div>
</div>
'
vueCode='
<dt-card class="d-w264">
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
</dt-card>
'
showHtmlWarning />
<h3 id="with-header-footer-and-scrollable-content" tabindex="-1" class="d-docsite--header-3">With Header, Footer and Scrollable Content <a class="header-anchor d-link d-docsite--link d-link" href="#with-header-footer-and-scrollable-content" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-card d-w264">
    <div class="d-card__header">
      <div class="d-headline--md">
        Lorem ipsum
      </div>
      <button type="button" class="d-btn d-btn--xs d-btn--circle">
        <dt-icon name="more-vertical" size="200" />
      </button>
    </div>
    <div class="d-card__content d-h72">
      <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
    </div>
    <div class="d-card__footer">
      <div class="d-d-flex d-g8">
        <button type="button" class="d-btn d-btn--sm d-btn--outlined">Button</button>
      </div>
    </div>
  </div>
</code-well-header>
<code-example-tabs
htmlCode='
<div class="d-card d-w264">
  <div class="d-card__header">
    <div class="d-headline--md">
      Lorem ipsum
    </div>
    <button type="button" class="d-btn d-btn--xs d-btn--circle">
      <dt-icon name="more-vertical" size="200" />
    </button>
  </div>
  <div class="d-card__content d-h84">
    <div>Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.</div>
  </div>
  <div class="d-card__footer">
    <div class="d-d-flex d-g8">
      <button type="button" class="d-btn d-btn--sm d-btn--outlined">Button</button>
    </div>
  </div>
</div>
'
vueCode='
<dt-card max-height="50px" class="d-w264">
  <template #header>
    <p class="d-headline-medium">Lorem ipsum</p>
      <dt-button
        size="xs"
        importance="clear"
        aria-label="Menu button"
      >
        <template #icon>
          <dt-icon
            name="more-vertical"
            size="100"
          />
        </template>
      </dt-button>
  </template>
  <template #content>
    Content slot. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec fermentum molestie semper. Morbi finibus nulla turpis, nec molestie mi rutrum.
  </template>
  <template #footer>
    <dt-button
      importance="outlined"
      size="sm"
    >
      Button
    </dt-button>
  </template>
</dt-card>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="card" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">At minimum, card contains body of content. It could also have header with buttons, and footer with buttons/text.</p>
<component-class-table component-name="card"></component-class-table></div></template>


